import { useEffect, useState, type MouseEvent } from 'react'
import { Folder, FolderOpen, Loader2, Settings } from 'lucide-react'
import { toast } from 'sonner'
import {
  EntityContextMenu,
  type EntityContextMenuEntry,
} from '@/components/features/entity-context-menu'
import { Button } from '@/components/ui/button'
import { SidebarErrorState } from '@/components/layout/sidebar/SidebarErrorState'
import { SidebarListSkeleton } from '@/components/layout/sidebar/SidebarListSkeleton'
import { SidebarTreeRow } from '@/components/layout/sidebar/SidebarTreeRow'
import { WorkspaceScopeSelector } from '@/components/layout/workspace-scope-selector'
import { wsNavRowPadLeftPx } from '@/components/layout/left-sidebar.components'
import { fetchWithAuth } from '@/lib/api'
import { getDocumentDisplayIcon } from '@/lib/document-icons'
import { openWorkspaceExtensionSettings } from '@/lib/open-settings'
import { testIds } from '@/lib/test-ids'
import { useWorkspace } from '@/providers/workspace-provider'
import { wsNavSectionStyle } from '@/lib/ws-nav'
import { dispatchNextcloudOpen, NEXTCLOUD_OPEN_EVENT, type NextcloudOpenDetail } from './nextcloud-browse-events'
import {
  buildNextcloudContextMenu,
  downloadNextcloudFile,
  openNextcloudEntry,
} from './nextcloud-context-menu'
import {
  NEXTCLOUD_SHARED_IN,
  NEXTCLOUD_SHARES_OUT,
  nextcloudParentPath,
  nextcloudPathContains,
} from './nextcloud-paths'

type NextcloudStatus = {
  enabled?: boolean
  connected?: boolean
  loginName?: string | null
  instanceUrl?: string | null
  version?: string | null
  defaultRoot?: string
}

type NextcloudEntry = {
  name: string
  path: string
  kind: 'file' | 'folder'
  fileId?: string | null
  encrypted?: boolean
}

type NextcloudShare = {
  id: string
  name: string
  path: string | null
  kind: 'file' | 'folder'
  owner: string | null
}

function instanceHost(url: string | null | undefined): string {
  if (!url) return ''
  try {
    return new URL(url).host
  } catch {
    return url
  }
}

function folderName(path: string): string {
  return path.split('/').filter(Boolean).pop() || 'Files'
}

/** Same folder tint as the sidebar file tree gallery. */
function folderIcon(expanded: boolean) {
  const Icon = expanded ? FolderOpen : Folder
  return <Icon className="h-3.5 w-3.5" style={{ color: 'hsl(var(--primary) / 0.8)' }} />
}

function fileIcon(path: string) {
  return getDocumentDisplayIcon({ filePath: path }, { className: 'h-3.5 w-3.5 shrink-0' })
}

async function readError(response: Response, fallback: string): Promise<string> {
  try {
    const body = (await response.json()) as { error?: string }
    return body.error || fallback
  } catch {
    return fallback
  }
}

function NextcloudFolderChildren({
  organizationId,
  path,
  depth,
  selectedPath,
  wsColor,
  onOpen,
  onContextMenu,
}: {
  organizationId: string
  path: string
  depth: number
  selectedPath: string | null
  wsColor: string | null
  onOpen: (entry: NextcloudEntry) => void
  onContextMenu: (event: MouseEvent, entry: NextcloudEntry) => void
}) {
  const [entries, setEntries] = useState<NextcloudEntry[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [attempt, setAttempt] = useState(0)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError(null)
    void (async () => {
      try {
        const response = await fetchWithAuth(
          `/api/nextcloud/files?organizationId=${encodeURIComponent(organizationId)}&path=${encodeURIComponent(path)}`,
        )
        if (!response.ok) throw new Error(await readError(response, 'Could not list that folder'))
        const data = (await response.json()) as { entries?: NextcloudEntry[] }
        if (!cancelled) setEntries(data.entries || [])
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Could not list that folder')
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [organizationId, path, attempt])

  if (loading) return <SidebarListSkeleton rows={depth === 0 ? 6 : 3} />
  if (error) {
    return (
      <SidebarErrorState
        message={error}
        onRetry={() => setAttempt((value) => value + 1)}
        className="py-6"
      />
    )
  }
  if (!entries?.length) {
    return (
      <p className="px-3 py-2 text-xs text-muted-foreground" style={{ paddingLeft: wsNavRowPadLeftPx(depth) }}>
        Empty folder
      </p>
    )
  }
  return (
    <>
      {entries.map((entry) =>
        entry.kind === 'folder' ? (
          <NextcloudFolder
            key={entry.path}
            organizationId={organizationId}
            entry={entry}
            depth={depth}
            selectedPath={selectedPath}
            wsColor={wsColor}
            onOpen={onOpen}
            onContextMenu={onContextMenu}
          />
        ) : (
          <SidebarTreeRow
            key={entry.path}
            padLeft={wsNavRowPadLeftPx(depth)}
            active={selectedPath === entry.path}
            icon={fileIcon(entry.path)}
            label={entry.name}
            subtitle={entry.encrypted ? 'Locked' : undefined}
            className="cursor-pointer"
            data-testid={testIds.nextcloud.treeRow}
            onClick={() => onOpen(entry)}
            onContextMenu={(event) => onContextMenu(event, entry)}
          />
        ),
      )}
    </>
  )
}

function NextcloudFolder({
  organizationId,
  entry,
  depth,
  startOpen = false,
  selectedPath,
  wsColor,
  onOpen,
  onContextMenu,
}: {
  organizationId: string
  entry: NextcloudEntry
  depth: number
  startOpen?: boolean
  selectedPath: string | null
  wsColor: string | null
  onOpen: (entry: NextcloudEntry) => void
  onContextMenu: (event: MouseEvent, entry: NextcloudEntry) => void
}) {
  const [open, setOpen] = useState(startOpen)
  const toggle = () => setOpen((value) => !value)
  return (
    <>
      <SidebarTreeRow
        padLeft={wsNavRowPadLeftPx(depth)}
        active={selectedPath === entry.path}
        contains={nextcloudPathContains(entry.path, selectedPath)}
        icon={folderIcon(open)}
        label={entry.name}
        subtitle={entry.encrypted ? 'Locked' : undefined}
        chevron={{
          expanded: open,
          onClick: (event) => {
            event.stopPropagation()
            toggle()
          },
        }}
        aria-expanded={open}
        className="cursor-pointer"
        data-testid={testIds.nextcloud.treeRow}
        onClick={() => onOpen(entry)}
        onContextMenu={(event) => onContextMenu(event, entry)}
      />
      {open && !entry.encrypted ? (
        <NextcloudFolderChildren
          organizationId={organizationId}
          path={entry.path}
          depth={depth + 1}
          selectedPath={selectedPath}
          wsColor={wsColor}
          onOpen={onOpen}
          onContextMenu={onContextMenu}
        />
      ) : null}
      {open && entry.encrypted ? (
        <p className="px-3 py-1 text-xs text-muted-foreground" style={{ paddingLeft: wsNavRowPadLeftPx(depth + 1) }}>
          Encrypted folders stay in Nextcloud.
        </p>
      ) : null}
    </>
  )
}

function NextcloudShareSection({
  organizationId,
  direction,
  label,
  selectedPath,
  wsColor,
  onOpen,
  onContextMenu,
}: {
  organizationId: string
  direction: 'in' | 'out'
  label: string
  selectedPath: string | null
  wsColor: string | null
  onOpen: (entry: NextcloudEntry) => void
  onContextMenu: (event: MouseEvent, entry: NextcloudEntry) => void
}) {
  const [open, setOpen] = useState(false)
  const [shares, setShares] = useState<NextcloudShare[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [attempt, setAttempt] = useState(0)

  useEffect(() => {
    if (!open) return
    let cancelled = false
    setLoading(true)
    setError(null)
    void (async () => {
      try {
        const response = await fetchWithAuth(
          `/api/nextcloud/shares?organizationId=${encodeURIComponent(organizationId)}&direction=${direction}`,
        )
        if (!response.ok) throw new Error(await readError(response, 'Could not list shares'))
        const data = (await response.json()) as { shares?: NextcloudShare[] }
        if (!cancelled) setShares(data.shares || [])
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Could not list shares')
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [open, organizationId, direction, attempt])

  const toggle = () => setOpen((value) => !value)
  const sectionPath = direction === 'in' ? NEXTCLOUD_SHARED_IN : NEXTCLOUD_SHARES_OUT

  return (
    <>
      <SidebarTreeRow
        padLeft={wsNavRowPadLeftPx(0)}
        active={selectedPath === sectionPath}
        icon={folderIcon(open)}
        label={label}
        chevron={{
          expanded: open,
          onClick: (event) => {
            event.stopPropagation()
            toggle()
          },
        }}
        aria-expanded={open}
        className="cursor-pointer"
        data-testid={testIds.nextcloud.treeRow}
        onClick={() => onOpen({ name: label, path: sectionPath, kind: 'folder' })}
      />
      {open && loading ? <SidebarListSkeleton rows={3} /> : null}
      {open && error ? (
        <SidebarErrorState
          message={error}
          onRetry={() => setAttempt((value) => value + 1)}
          className="py-4"
        />
      ) : null}
      {open && !loading && !error && shares?.length === 0 ? (
        <p className="px-3 py-1 text-xs text-muted-foreground" style={{ paddingLeft: wsNavRowPadLeftPx(1) }}>
          None
        </p>
      ) : null}
      {open && !loading && !error
        ? shares?.map((share) =>
            share.kind === 'folder' && share.path ? (
              <NextcloudFolder
                key={share.id}
                organizationId={organizationId}
                entry={{ name: share.name, path: share.path, kind: 'folder' }}
                depth={1}
                selectedPath={selectedPath}
                wsColor={wsColor}
                onOpen={onOpen}
                onContextMenu={onContextMenu}
              />
            ) : (
              <SidebarTreeRow
                key={share.id}
                padLeft={wsNavRowPadLeftPx(1)}
                active={selectedPath === share.path}
                icon={fileIcon(share.path || share.name)}
                label={share.name}
                subtitle={direction === 'in' && share.owner ? share.owner : undefined}
                className="cursor-pointer"
                data-testid={testIds.nextcloud.treeRow}
                onClick={() => {
                  if (!share.path) return
                  onOpen({ name: share.name, path: share.path, kind: 'file' })
                }}
                onContextMenu={(event) => {
                  if (!share.path) return
                  onContextMenu(event, { name: share.name, path: share.path, kind: 'file' })
                }}
              />
            ),
          )
        : null}
    </>
  )
}

export function NextcloudSidebarPanel({
  organizationId,
  organizationIds,
  onOrganizationIdChange,
}: {
  organizationId?: string
  organizationIds?: string[]
  onOrganizationIdChange?: (organizationId: string) => void
}) {
  const { workspaces } = useWorkspace()
  const wsColor = workspaces.find((workspace) => workspace.id === organizationId)?.color ?? null
  const [status, setStatus] = useState<NextcloudStatus | null>(null)
  const [loading, setLoading] = useState(true)
  const [selectedPath, setSelectedPath] = useState<string | null>(null)
  const [menu, setMenu] = useState<{ x: number; y: number; entry: NextcloudEntry } | null>(null)

  useEffect(() => {
    const handler = (event: Event) => {
      const detail = (event as CustomEvent<NextcloudOpenDetail>).detail
      if (!detail?.path) return
      if (organizationId && detail.organizationId !== organizationId) return
      setSelectedPath(detail.path)
    }
    window.addEventListener(NEXTCLOUD_OPEN_EVENT, handler)
    return () => window.removeEventListener(NEXTCLOUD_OPEN_EVENT, handler)
  }, [organizationId])

  useEffect(() => {
    if (!organizationId) {
      setStatus(null)
      setSelectedPath(null)
      setLoading(false)
      return
    }
    let cancelled = false
    setLoading(true)
    setSelectedPath(null)
    void (async () => {
      try {
        const response = await fetchWithAuth(
          `/api/nextcloud/status?organizationId=${encodeURIComponent(organizationId)}`,
        )
        if (!response.ok) throw new Error('status')
        const data = (await response.json()) as NextcloudStatus
        if (!cancelled) setStatus(data)
      } catch {
        if (!cancelled) setStatus(null)
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [organizationId])

  const host = instanceHost(status?.instanceUrl)
  const rootPath = status?.defaultRoot?.trim() || '/'
  const accountCandidates = organizationIds ?? []
  const showAccountPicker = accountCandidates.length > 1
  const openEntry = (entry: NextcloudEntry) => {
    if (!organizationId || entry.encrypted) return
    if (entry.kind === 'file') {
      openDocument(entry)
      return
    }
    setSelectedPath(entry.path)
    dispatchNextcloudOpen({
      organizationId,
      path: entry.path,
      kind: 'folder',
      name: entry.name,
      fileId: entry.fileId,
      encrypted: entry.encrypted,
    })
  }

  const openDocument = (entry: NextcloudEntry) => {
    if (!organizationId || entry.encrypted) return
    if (entry.kind === 'folder') {
      openEntry(entry)
      return
    }
    setSelectedPath(entry.path)
    dispatchNextcloudOpen({
      organizationId,
      path: entry.path,
      kind: 'file',
      name: entry.name,
      fileId: entry.fileId,
      encrypted: entry.encrypted,
      surface: 'document',
    })
  }

  const previewEntry = (entry: NextcloudEntry) => {
    if (!organizationId) return
    if (entry.kind === 'folder') {
      openEntry(entry)
      return
    }
    const parent = nextcloudParentPath(entry.path)
    setSelectedPath(entry.path)
    dispatchNextcloudOpen({
      organizationId,
      path: parent,
      kind: 'folder',
      name: parent === '/' ? 'All files' : parent.split('/').filter(Boolean).pop() || 'Files',
      previewPath: entry.path,
      fileId: entry.fileId,
    })
  }

  const onRowContextMenu = (event: MouseEvent, entry: NextcloudEntry) => {
    event.preventDefault()
    setSelectedPath(entry.path)
    setMenu({ x: event.clientX, y: event.clientY, entry })
  }

  const menuEntries: EntityContextMenuEntry[] = menu && organizationId
    ? buildNextcloudContextMenu({
      entry: menu.entry,
      onPreview: () => previewEntry(menu.entry),
      onOpen: () => openDocument(menu.entry),
      onOpenExternal: () => openNextcloudEntry(status?.instanceUrl, menu.entry),
      onDownload: () => {
        void downloadNextcloudFile(organizationId, menu.entry).catch((err: unknown) => {
          toast(err instanceof Error ? err.message : 'Could not download that file')
        })
      },
    })
    : []

  return (
    <div
      className="ws-nav-section flex min-h-0 flex-1 flex-col overflow-hidden"
      style={wsNavSectionStyle(wsColor)}
      data-testid={testIds.nextcloud.sidebarPanel}
    >
      {showAccountPicker ? (
        <div className="shrink-0 space-y-1 px-2 pb-2 pt-2">
          <p className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
            Nextcloud account
          </p>
          <WorkspaceScopeSelector
            mode="single"
            candidateIds={accountCandidates}
            scope={organizationId ? [organizationId] : null}
            onScopeChange={(scope) => {
              if (scope?.[0]) onOrganizationIdChange?.(scope[0])
            }}
            fallbackScope={accountCandidates[0] ? [accountCandidates[0]] : null}
            label="Nextcloud account"
            description="Each workspace has its own Nextcloud login."
            tooltip="Nextcloud account for this tree"
            triggerClassName="w-full justify-between"
            triggerTestId={testIds.nextcloud.accountSelect}
          />
        </div>
      ) : null}
      {loading ? (
        <div className="px-3 py-3">
          <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
        </div>
      ) : status?.connected && organizationId ? (
        <>
          <div className="flex items-start gap-1 px-3 pb-2 pt-3">
            <div className="min-w-0 flex-1 space-y-0.5">
              <p className="truncate text-sm font-medium text-sidebar-foreground">
                {status.loginName || 'Nextcloud'}
              </p>
              <p className="truncate text-xs text-muted-foreground">
                {[host, status.version ? `Nextcloud ${status.version}` : '']
                  .filter(Boolean)
                  .join(' · ')}
              </p>
            </div>
            <button
              type="button"
              className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded text-sidebar-foreground/60 hover:bg-sidebar-accent/60 hover:text-sidebar-foreground"
              title="Nextcloud settings"
              aria-label="Nextcloud settings"
              data-testid={testIds.nextcloud.accountSettings}
              onClick={() => openWorkspaceExtensionSettings('nextcloud', organizationId)}
            >
              <Settings className="h-3.5 w-3.5" />
            </button>
          </div>
          <div className="min-h-0 flex-1 overflow-auto pb-3" data-testid={testIds.nextcloud.fileTree}>
            <NextcloudFolder
              organizationId={organizationId}
              entry={{
                name: rootPath === '/' ? 'Files' : folderName(rootPath),
                path: rootPath,
                kind: 'folder',
              }}
              depth={0}
              startOpen
              selectedPath={selectedPath}
              wsColor={wsColor}
              onOpen={openEntry}
              onContextMenu={onRowContextMenu}
            />
            <NextcloudShareSection
              organizationId={organizationId}
              direction="in"
              label="Shared with me"
              selectedPath={selectedPath}
              wsColor={wsColor}
              onOpen={openEntry}
              onContextMenu={onRowContextMenu}
            />
            <NextcloudShareSection
              organizationId={organizationId}
              direction="out"
              label="Shares you created"
              selectedPath={selectedPath}
              wsColor={wsColor}
              onOpen={openEntry}
              onContextMenu={onRowContextMenu}
            />
          </div>
        </>
      ) : (
        <div className="space-y-3 px-3 py-3">
          <p className="text-sm text-muted-foreground">
            Sign in to browse this Nextcloud account.
          </p>
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() => openWorkspaceExtensionSettings('nextcloud', organizationId)}
          >
            Open settings
          </Button>
        </div>
      )}
      {menu ? (
        <EntityContextMenu
          x={menu.x}
          y={menu.y}
          entries={menuEntries}
          onClose={() => setMenu(null)}
        />
      ) : null}
    </div>
  )
}
